import { API_URL } from '../config/api';
import { toast } from 'react-toastify';

const LOCAL_AGENT_URL = 'http://localhost:9111';

/**
 * Lokal yazici agent'ina ulasilabilir mi kontrol et
 */
let agentAvailable = null;
let agentCheckTime = 0;

export const checkLocalAgent = async () => {
  if (agentAvailable !== null && Date.now() - agentCheckTime < 30000) {
    return agentAvailable;
  }
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    const res = await fetch(`${LOCAL_AGENT_URL}/api/ping`, { signal: controller.signal });
    clearTimeout(timeout);
    const data = await res.json();
    agentAvailable = data.ok === true;
  } catch {
    agentAvailable = false;
  }
  agentCheckTime = Date.now();
  return agentAvailable;
};

/**
 * Lokal agent veya sunucu uzerinden yazici listesini al
 */
export const fetchPrintersFromAgent = async () => {
  const hasAgent = await checkLocalAgent();
  if (hasAgent) {
    try {
      const res = await fetch(`${LOCAL_AGENT_URL}/api/printers`);
      const data = await res.json();
      if (data.success && data.printers && data.printers.length > 0) {
        return { printers: data.printers, source: 'local' };
      }
    } catch {}
  }

  try {
    const res = await fetch(`${API_URL}/settings/printers`);
    const data = await res.json();
    if (data.success && data.printers && data.printers.length > 0) {
      return { printers: data.printers, source: 'server' };
    }
  } catch {}

  return { printers: [], source: 'none' };
};

/**
 * Fiş HTML'ine kaynak doğrulama scripti enjekte eder.
 * - Tüm görsellerin (logo vb.) yüklenmesini bekler
 * - Barkod SVG'lerinin oluşturulmasını bekler
 * - Puppeteer için window.__resourcesReady flag'i set eder
 * - Popup modunda window.print()'i override ederek kaynaklar hazır olana dek bekletir
 */
function injectResourceCheck(html) {
  const tag = 'script';
  const scriptClose = `</${tag}>`;
  const checkScript = `<script>
(function() {
  var _origPrint = window.print.bind(window);
  var _printQueued = false;

  function checkAllReady() {
    var imgs = document.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) return false;
    }
    var svgs = document.querySelectorAll('svg[id*="barcode"]');
    for (var j = 0; j < svgs.length; j++) {
      if (svgs[j].childNodes.length === 0) return false;
    }
    return true;
  }

  function waitAndExecute(cb) {
    var attempts = 0;
    var startTime = Date.now();
    function poll() {
      var elapsed = Date.now() - startTime;
      var imgsReady = true;
      var imgs = document.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].complete) { imgsReady = false; break; }
      }
      var barcodesReady = true;
      if (elapsed < 3000) {
        var svgs = document.querySelectorAll('svg[id*="barcode"]');
        for (var j = 0; j < svgs.length; j++) {
          if (svgs[j].childNodes.length === 0) { barcodesReady = false; break; }
        }
      }
      if ((imgsReady && barcodesReady) || attempts >= 80) {
        window.__resourcesReady = true;
        if (cb) cb();
      } else {
        attempts++;
        setTimeout(poll, 100);
      }
    }
    poll();
  }

  window.print = function() {
    if (_printQueued) return;
    _printQueued = true;
    waitAndExecute(function() { _origPrint(); });
  };

  window.addEventListener('load', function() {
    setTimeout(function() { waitAndExecute(null); }, 300);
  });
})();
` + scriptClose;

  if (html.match(/<head>/i)) {
    return html.replace(/<head>/i, '<head>' + checkScript);
  }
  return checkScript + html;
}

/**
 * Görsellerin göreli yollarını mutlak URL'lere dönüştürür.
 * Puppeteer setContent() ile yüklenen sayfalarda göreli yollar çözümlenemez,
 * bu yüzden Puppeteer yoluyla yazdırırken mutlak URL'ler kullanılmalıdır.
 */
function makeImagePathsAbsolute(html) {
  try {
    const origin = window.location.origin;
    return html.replace(/src="\/([^"]*?)"/g, `src="${origin}/$1"`);
  } catch {
    return html;
  }
}

/**
 * Direkt yazdirma veya onizleme modunda yazdir
 * Oncelik sirasi:
 * 1) Electron IPC (popup acmadan dogrudan yazdirir)
 * 2) Lokal print-agent (localhost:9111)
 * 3) Sunucu API (/api/print/direct)
 * 4) Fallback: popup pencere + window.print()
 */
export const handlePrint = async (html, type = 'receipt', options = {}) => {
  // Kaynak doğrulama scripti enjekte et
  html = injectResourceCheck(html);

  const directPrintEnabled = localStorage.getItem('directPrintEnabled') === 'true';
  const isElectron = !!(window.electronAPI && window.electronAPI.isElectron);

  const printerName = type === 'label'
    ? localStorage.getItem('labelPrinter')
    : type === 'repair'
    ? localStorage.getItem('repairPrinter')
    : localStorage.getItem('receiptPrinter');

  if (directPrintEnabled) {
    // Puppeteer yolu: göreli image yollarını mutlak URL'lere çevir
    const absoluteHtml = makeImagePathsAbsolute(html);

    if (!printerName) {
      toast.warning(
        type === 'label'
          ? 'Etiket yazıcısı seçilmedi! Ayarlar > Yazıcı sayfasından seçin.'
          : type === 'repair'
          ? 'Tamirat yazıcısı seçilmedi! Ayarlar > Yazıcı sayfasından seçin.'
          : 'Fiş yazıcısı seçilmedi! Ayarlar > Yazıcı sayfasından seçin.'
      );
      // Fallback'e düş
    } else {

      // 1) Electron: IPC ile doğrudan yazdır (popup yok, dialog yok)
      if (isElectron && window.electronAPI.printHTML) {
        try {
          const result = await window.electronAPI.printHTML(absoluteHtml, printerName, type);
          if (result.success) {
            toast.success('Yazıcıya gönderildi', { autoClose: 2250 });
            return true;
          }
          console.error('Electron print failed:', result.failureReason);
          toast.error(`Yazdırma hatası: ${result.failureReason || 'Bilinmeyen hata'}`);
        } catch (e) {
          console.error('Electron print error:', e);
          toast.error('Electron yazdırma hatası');
        }
        return false; // Electron'da fallback popup açma
      }

      // 2) Web: Lokal agent dene
      const hasAgent = await checkLocalAgent();
      if (hasAgent) {
        try {
          const res = await fetch(`${LOCAL_AGENT_URL}/api/print`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ html: absoluteHtml, printerName, type, pageWidth: options.pageWidth, pageHeight: options.pageHeight })
          });
          const result = await res.json();
          if (result.success) {
            toast.success('Yazıcıya gönderildi', { autoClose: 2250 });
            return true;
          }
          console.error('Local agent print failed:', result.error);
        } catch (e) {
          console.error('Local agent print error:', e);
        }
      }

      // 3) Web: Sunucu API dene
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/print/direct`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ html: absoluteHtml, printerName, type, pageWidth: options.pageWidth, pageHeight: options.pageHeight })
        });
        const result = await res.json();
        if (result.success) {
          toast.success('Yazıcıya gönderildi', { autoClose: 2250 });
          return true;
        }
        console.error('Server print failed:', result.error);
        toast.error(`Yazdırma hatası: ${result.error || 'Bilinmeyen hata'}`);
      } catch (e) {
        console.error('Server print error:', e);
      }
    }
  }

  // Fallback: Popup pencere aç (önizleme modu veya tüm yöntemler başarısız)
  // Blob URL'de relative path'ler çözümlenemez — mutlak URL'lere çevir
  try {
    const blob = new Blob([makeImagePathsAbsolute(html)], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const windowFeatures = options.windowOptions !== undefined ? options.windowOptions : 'width=400,height=600';
    const printWindow = windowFeatures
      ? window.open(blobUrl, '_blank', windowFeatures)
      : window.open(blobUrl, '_blank');
    if (printWindow) {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } else {
      URL.revokeObjectURL(blobUrl);
    }
  } catch (e) {
    console.error('Popup açma hatası:', e);
  }
  return false;
};
